import moment from "moment";
import {
  TableCellType,
  TableHeader,
} from "../../../../components/custom-table/table";
import { FinalWorklistDataResponse } from "../../../../data-model/finalWorklistModel";
import { LocalStorageKeys } from "../../../../enums/localStorageKeys";
import {
  FinalWorklistOrderFilterKeys,
  FinalWorklistOrderStatusValues,
} from "../../../../enums/medicalPaKeys";
import { OrganizationType } from "../../../../enums/organizationTypes";
import { MedicalPaOrdersTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";

import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import {
  getAuthStatusOfFinalWorklist,
  getAuthStatusOfMedicalPaOrder,
} from "../../../../utils/getAuthStatusOfMedicalPaOrder";
import { getFinalStatusOfOrder } from "../../../../utils/getFinalStatusOfOrder";
import { getStatusOfOrder } from "../../../../utils/getStatusOfOrder";
import { getItemFromLocalStorage } from "../../../../utils/localStorageHelper";
import { capitalizeString } from "../../../../utils/stringModifications";

export const FinalWorklistTableHeaders: TableHeader[] = [
  {
    label: "Patient Name",
    key: MedicalPaOrdersTableColumnKeys.PATIENT_DETAILS,
    order: 1,
    width: 15,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Plan Details",
    key: MedicalPaOrdersTableColumnKeys.PLAN_DETAILS,
    order: 2,
    width:
      getItemFromLocalStorage(
        LocalStorageKeys.INTERNAL_ORGANIZATION_FACILITY_ID,
      ) === OrganizationType.NYCBS
        ? 15
        : 10,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Medication",
    key: MedicalPaOrdersTableColumnKeys.MEDICATION,
    order: 3,
    width:
      getItemFromLocalStorage(
        LocalStorageKeys.INTERNAL_ORGANIZATION_FACILITY_ID,
      ) === OrganizationType.NYCBS
        ? 20
        : 15,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Date of Service",
    key: MedicalPaOrdersTableColumnKeys.DATE_OF_SERVICE,
    order: 4,
    width: 10,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "Date of Work",
    key: MedicalPaOrdersTableColumnKeys.CREATED_AT,
    order: 5,
    width: 10,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "Location",
    key: MedicalPaOrdersTableColumnKeys.LOCATION,
    order: 6,
    width:
      getItemFromLocalStorage(
        LocalStorageKeys.INTERNAL_ORGANIZATION_FACILITY_ID,
      ) === OrganizationType.NYCBS
        ? 0
        : 10,
    sortable:
      getItemFromLocalStorage(
        LocalStorageKeys.INTERNAL_ORGANIZATION_FACILITY_ID,
      ) === OrganizationType.NYCBS
        ? false
        : true,
    type: TableCellType.STRING,
  },
  {
    label: "Auth Status",
    key: MedicalPaOrdersTableColumnKeys.AUTH_VERIFICATION,
    order: 7,
    width: 8,
    type: TableCellType.BADGE,
  },

  {
    label: "BO Value",
    key: MedicalPaOrdersTableColumnKeys.BO_VALUE,
    order: 8,
    width: 12,
    type: TableCellType.BADGE,
  },
  {
    label: "Audit Trail",
    key: MedicalPaOrdersTableColumnKeys.PDF_EMBED_PAGE,
    order: 9,
    width: 10,
    type: TableCellType.DOWNLOAD_BUTTON,
  },
];

export const FinalWorklistTableHeadersForNYCBS: TableHeader[] = [
  {
    label: "Patient Name",
    key: MedicalPaOrdersTableColumnKeys.PATIENT_DETAILS,
    order: 1,
    width: 15,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Plan Details",
    key: MedicalPaOrdersTableColumnKeys.PLAN_DETAILS,
    order: 2,
    width: 15,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Medication",
    key: MedicalPaOrdersTableColumnKeys.MEDICATION,
    order: 3,
    width: 20,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Date of Service",
    key: MedicalPaOrdersTableColumnKeys.DATE_OF_SERVICE,
    order: 4,
    width: 10,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "Date of Work",
    key: MedicalPaOrdersTableColumnKeys.CREATED_AT,
    order: 5,
    width: 10,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "Auth Status",
    key: MedicalPaOrdersTableColumnKeys.AUTH_VERIFICATION,
    order: 7,
    width: 8,
    type: TableCellType.BADGE,
  },

  {
    label: "BO Value",
    key: MedicalPaOrdersTableColumnKeys.BO_VALUE,
    order: 8,
    width: 12,
    type: TableCellType.BADGE,
  },
  {
    label: "Audit Trail",
    key: MedicalPaOrdersTableColumnKeys.PDF_EMBED_PAGE,
    order: 9,
    width: 10,
    type: TableCellType.DOWNLOAD_BUTTON,
  },
];

export const generatFinalWorklistTableData = (
  medicalPaOrders: FinalWorklistDataResponse[],
  authStatusOptions: AuthStatusOptionModel[],
) => {
  return medicalPaOrders.map((medicalPaOrder) => {
    const authStatus = getAuthStatusOfFinalWorklist(
      medicalPaOrder,
      authStatusOptions,
    );

    const status = getStatusOfOrder(medicalPaOrder?.bo_value);
    const finalStatus = getFinalStatusOfOrder(medicalPaOrder?.bo_value);
    const getLocation = () => {
      if (
        medicalPaOrder?.location === "" ||
        medicalPaOrder?.location === null ||
        medicalPaOrder?.location === undefined
      ) {
        return "No Location";
      }
      return medicalPaOrder?.location;
    };

    return {
      [MedicalPaOrdersTableColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(
          generateFullName(medicalPaOrder?.patient_name ?? "", "", ""),
        ),
        secondaryText: `MRN : ${medicalPaOrder?.mrn ?? ""}`,
      },
      [MedicalPaOrdersTableColumnKeys.DATE_OF_SERVICE]:
        medicalPaOrder?.date_of_service ?? "",
      [MedicalPaOrdersTableColumnKeys.PLAN_DETAILS]:
        medicalPaOrder?.primary_insurance ?? "",
      [MedicalPaOrdersTableColumnKeys.MEDICATION]:
        medicalPaOrder?.regimen ?? "",
      [MedicalPaOrdersTableColumnKeys.ASSIGNEE]: {
        text: medicalPaOrder?.rep_name ?? "",
        id: medicalPaOrder?.id ?? "",
      },
      [MedicalPaOrdersTableColumnKeys.LOCATION]: getLocation(),
      [MedicalPaOrdersTableColumnKeys.ROW_DATA]: JSON.stringify(medicalPaOrder),
      [MedicalPaOrdersTableColumnKeys.ID]: medicalPaOrder?.id ?? "",
      [MedicalPaOrdersTableColumnKeys.CREATED_AT]:
        moment(medicalPaOrder?.date_of_work).format("MM/DD/YYYY") ?? "",
      [MedicalPaOrdersTableColumnKeys.AUTH_VERIFICATION]: {
        text: authStatus.text,
        color: authStatus.textColor,
        bgColor: authStatus.bgColor,
        displayText: authStatus.text,
      },
      [MedicalPaOrdersTableColumnKeys.STATUS]: {
        text: status.text,
        color: status.color,
        bgColor: status.bgColor,
        displayText: status.text,
      },
      [MedicalPaOrdersTableColumnKeys.BO_VALUE]: {
        text: finalStatus.text,
        color: finalStatus.color,
        bgColor: finalStatus.bgColor,
        displayText: finalStatus.text,
        hoverText:
          finalStatus.text.toLowerCase() === "denied" ||
          finalStatus.text.toLowerCase() === "authmate denied"
            ? medicalPaOrder?.denial_comments
            : medicalPaOrder?.auth_issue_comments,
        showInfoIcon: shouldShowInfoIcon(medicalPaOrder),
      },
      [MedicalPaOrdersTableColumnKeys.SELECT]: {
        id: medicalPaOrder?.id ?? "",
        label: "",
      },
      [MedicalPaOrdersTableColumnKeys.PDF_EMBED_PAGE]: {
        id: medicalPaOrder?.upload_id ?? "",
      },
    };
  });
};

export const generateMedicalPaOrdersTableDataForNonAdmin = (
  medicalPaOrders: FinalWorklistDataResponse[],
  authStatusOptions: AuthStatusOptionModel[],
) => {
  const getLocation = (medicalPaOrder: FinalWorklistDataResponse) => {
    if (
      medicalPaOrder?.location === "" ||
      medicalPaOrder?.location === null ||
      medicalPaOrder?.location === undefined
    ) {
      return "No Location";
    }
    return medicalPaOrder?.location;
  };
  return medicalPaOrders.map((medicalPaOrder) => {
    const authStatus = getAuthStatusOfMedicalPaOrder(
      medicalPaOrder,
      authStatusOptions,
    );
    const status = getStatusOfOrder(medicalPaOrder?.bo_value);
    const finalStatus = getFinalStatusOfOrder(medicalPaOrder?.bo_value);
    return {
      [MedicalPaOrdersTableColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(
          generateFullName(medicalPaOrder?.patient_name ?? "", "", ""),
        ),
        secondaryText: `MRN : ${medicalPaOrder?.mrn ?? ""}`,
      },
      [MedicalPaOrdersTableColumnKeys.DATE_OF_SERVICE]:
        medicalPaOrder?.date_of_service ?? "",
      [MedicalPaOrdersTableColumnKeys.PLAN_DETAILS]:
        medicalPaOrder?.primary_insurance ?? "",
      [MedicalPaOrdersTableColumnKeys.MEDICATION]:
        medicalPaOrder?.regimen ?? "",
      [MedicalPaOrdersTableColumnKeys.ASSIGNEE]:
        capitalizeString(medicalPaOrder?.rep_name) ?? "No Location",
      [MedicalPaOrdersTableColumnKeys.LOCATION]: getLocation(medicalPaOrder),
      [MedicalPaOrdersTableColumnKeys.ROW_DATA]: JSON.stringify(medicalPaOrder),
      [MedicalPaOrdersTableColumnKeys.ID]: medicalPaOrder?.id ?? "",
      [MedicalPaOrdersTableColumnKeys.CREATED_AT]:
        moment(medicalPaOrder?.date_of_work).format("MM/DD/YYYY") ?? "",
      [MedicalPaOrdersTableColumnKeys.AUTH_VERIFICATION]: {
        text: authStatus.text,
        color: authStatus.textColor,
        bgColor: authStatus.bgColor,
        displayText: authStatus.text,
      },
      [MedicalPaOrdersTableColumnKeys.BO_VALUE]: {
        text: finalStatus.text,
        color: finalStatus.color,
        bgColor: finalStatus.bgColor,
        displayText: finalStatus.text,
        hoverText:
          finalStatus.text.toLowerCase() === "denied" ||
          finalStatus.text.toLowerCase() === "authmate denied"
            ? medicalPaOrder?.denial_comments
            : finalStatus.text.toLowerCase() === "authmate issue"
              ? medicalPaOrder?.denial_comments
              : medicalPaOrder?.auth_issue_comments,
        showInfoIcon: shouldShowInfoIcon(medicalPaOrder),
      },
      [MedicalPaOrdersTableColumnKeys.STATUS]: {
        text: status.text,
        color: status.color,
        bgColor: status.bgColor,
        displayText: status.text,
      },
      [MedicalPaOrdersTableColumnKeys.SELECT]: {
        id: medicalPaOrder?.id ?? "",
        label: "",
      },
      [MedicalPaOrdersTableColumnKeys.PDF_EMBED_PAGE]: {
        id: medicalPaOrder?.upload_id ?? "",
      },
    };
  });
};

export const createFilterForOrderStatus = (analyticsData?: any) => {
  let options: Array<{ label: string; value: string }> = [];

  if (analyticsData?.data?.distributions?.bo_value) {
    options = analyticsData.data.distributions.bo_value.map((item: any) => ({
      label: item.category,
      value: item.category,
    }));
  } else {
    options = [
      {
        label: "Denied",
        value: FinalWorklistOrderStatusValues.Denied,
      },
      {
        label: "Authorized",
        value: FinalWorklistOrderStatusValues.Authorized,
      },
      {
        label: "Financial",
        value: FinalWorklistOrderStatusValues.Financial,
      },
      {
        label: "Hold",
        value: FinalWorklistOrderStatusValues.Hold,
      },
      {
        label: "Authmate Pending",
        value: FinalWorklistOrderStatusValues.AuthmatePending,
      },
      {
        label: "Authmate Issue",
        value: FinalWorklistOrderStatusValues.AuthmateIssue,
      },
    ];
  }

  return {
    id: FinalWorklistOrderFilterKeys.Status,
    label: "BO Value",
    options: options.sort((a, b) => a.label.localeCompare(b.label)),
    type: "string" as const,
  };
};

export const createFilterForAuthStatus = (analyticsData?: any) => {
  let options: Array<{ label: string; value: string }> = [];

  if (analyticsData?.data?.distributions?.auth_status) {
    options = analyticsData.data.distributions.auth_status.map((item: any) => ({
      label: item.category,
      value: item.category,
    }));
  }

  return {
    id: FinalWorklistOrderFilterKeys.AuthStatus,
    label: "Auth Status",
    options: options.sort((a, b) => a.label.localeCompare(b.label)),
    type: "string" as const,
  };
};

// const getUniqueDrugNamesFromFinalWorklist = (
//   orders: FinalWorklistDataResponse[],
// ) => {
//   const drugNames = new Set<string>();
//   orders.forEach((order) => {
//     if (order.regimen) {
//       drugNames.add(order.regimen);
//     }
//   });
//   return Array.from(drugNames).filter(Boolean);
// };

export const createFilterForDrugNames = (analyticsData?: any) => {
  let options: Array<{ label: string; value: string }> = [];

  if (analyticsData?.data?.distributions?.regimen) {
    options = analyticsData.data.distributions.regimen.map((item: any) => ({
      label: item.category,
      value: item.category,
    }));
  }

  return {
    id: FinalWorklistOrderFilterKeys.DrugNames,
    label: "Medication",
    options: options.sort((a, b) => a.label.localeCompare(b.label)),
    type: "string" as const,
  };
};

export const createFilterForLocation = (analyticsData?: any) => {
  let options: Array<{ label: string; value: string }> = [];

  if (analyticsData?.data?.distributions?.location) {
    options = analyticsData.data.distributions.location.map((item: any) => ({
      label: item.category === "Unknown" ? "No Location" : item.category,
      value: item.category,
    }));
  } else {
    options = [];
  }

  return {
    id: FinalWorklistOrderFilterKeys.Location,
    label: "Location",
    options: options.sort((a, b) => a.label.localeCompare(b.label)),
    type: "string" as const,
  };
};

export const createFilterForPrimaryInsurance = (analyticsData?: any) => {
  let options: Array<{ label: string; value: string }> = [];

  if (analyticsData?.data?.distributions?.primary_insurance) {
    options = analyticsData.data.distributions.primary_insurance.map(
      (item: any) => ({
        label: item.category ?? "",
        value: item.category ?? "",
      }),
    );
  } else {
    options = [];
  }

  return {
    id: FinalWorklistOrderFilterKeys.PrimaryInsurance,
    label: "Insurance Plans",
    options: options.sort((a, b) => a.label.localeCompare(b.label)),
    type: "string" as const,
  };
};

export const createFilterForDateOfService = () => {
  return {
    id: FinalWorklistOrderFilterKeys.DateOfService,
    label: "Date of Service",
    options: [],
    type: "date" as const,
  };
};

export const createFilterForCreatedAt = () => {
  return {
    id: FinalWorklistOrderFilterKeys.CreatedAt,
    label: "Date of Work",
    options: [],
    type: "date" as const,
  };
};

const ifAuthIssue = (boValue: string) => {
  return (
    boValue?.toLowerCase() === "auth issue (onsite)" ||
    boValue?.toLowerCase() === "auth issue" ||
    boValue?.toLowerCase() === "authmate issue (onsite)" ||
    boValue?.toLowerCase() === "authmate issue"
  );
};

const shouldShowInfoIcon = (orderData: FinalWorklistDataResponse) => {
  const checkIfDenied =
    (orderData?.bo_value?.toLowerCase() === "authmate denied" ||
      orderData?.bo_value?.toLowerCase() === "denied") &&
    orderData?.denial_comments !== "";
  const checkIfAuthIssue =
    ifAuthIssue(orderData?.bo_value) &&
    orderData?.auth_issue_comments &&
    orderData?.auth_issue_comments !== "";
  return checkIfDenied || checkIfAuthIssue;
};
